import type { JSX } from 'react'
import { useTheme } from '@mui/material/styles'
import { runwayDesignator, runwayWind } from '@jupi/shared'

// Ported from webplan-modern's features/crosswind/CrosswindDiagram.tsx: the
// runway on a compass rose, the wind streaming across it, and the
// crosswind component drawn off the threshold. The arithmetic is the
// shared crosswind module's; this file only draws it.

/** Shared colours so the diagram, its legend and the result cards all match. */
export const CW_COLORS = {
  wind: '#9b7fd9', // light purple: the wind (from) vector
  crosswind: '#f5a623', // orange: the crosswind component
  aircraft: '#1565c0', // blue: the aircraft and the runway in use
  intoWind: '#0f9d8f', // teal: the into-wind runway end
}

interface Props {
  runwayHdg: number
  windDir: number
  windSpeed: number
}

const SIZE = 360
const C = SIZE / 2 // centre
const R = 150 // compass outer radius
const RWY_LEN = 214
const RWY_W = 26

const pad3 = (n: number) => String(((Math.round(n) % 360) + 360) % 360).padStart(3, '0')

/** Compass-bearing polar → cartesian (0° = up/north, clockwise). */
function pol(brg: number, r: number): [number, number] {
  const a = (brg * Math.PI) / 180
  return [C + r * Math.sin(a), C - r * Math.cos(a)]
}

// A compact top-down GA aircraft silhouette, nose at -y, ~16 units wingspan.
const PLANE_PATH =
  'M0,-8 C1,-8 1.4,-6 1.4,-4 L1.4,-2 L8,1.4 L8,3 L1.4,0.4 L1.4,4 L3.2,6 L3.2,7 L0,6 L-3.2,7 L-3.2,6 L-1.4,4 L-1.4,0.4 L-8,3 L-8,1.4 L-1.4,-2 L-1.4,-4 C-1.4,-6 -1,-8 0,-8 Z'

export function CrosswindDiagram({ runwayHdg, windDir, windSpeed }: Props) {
  const theme = useTheme()

  const muted = theme.palette.text.secondary
  const faint = theme.palette.divider
  const cardinal = theme.palette.error.main
  const rwyGrey = '#9aa7b0'

  const recip = (runwayHdg + 180) % 360
  const desThis = runwayDesignator(runwayHdg)
  const desRecip = runwayDesignator(recip)

  const wind = runwayWind(runwayHdg, windDir, windSpeed)
  const xw = wind.crosswindKt
  const landPrimary = wind.headwindKt >= 0
  const approachBrg = landPrimary ? recip : runwayHdg

  // ticks
  const ticks: JSX.Element[] = []
  for (let d = 0; d < 360; d += 5) {
    const major = d % 30 === 0
    const [x1, y1] = pol(d, R)
    const [x2, y2] = pol(d, R - (major ? 12 : 6))
    ticks.push(<line key={d} x1={x1} y1={y1} x2={x2} y2={y2} stroke={faint} strokeWidth={major ? 1.4 : 0.7} />)
  }
  const degLabels: JSX.Element[] = []
  for (let d = 0; d < 360; d += 30) {
    const [x, y] = pol(d, R - 24)
    degLabels.push(
      <text key={d} x={x} y={y} fontSize={9} fill={muted} textAnchor="middle" dominantBaseline="central">
        {pad3(d)}
      </text>,
    )
  }
  const cardinals: [string, number][] = [
    ['N', 0],
    ['E', 90],
    ['S', 180],
    ['W', 270],
  ]

  // wind shaft: static line from the source rim in toward the centre
  const [wShaftX1, wShaftY1] = pol(windDir, R - 2)
  const [wShaftX2, wShaftY2] = pol(windDir, 4)

  // animated double-chevrons streaming downwind along the wind line
  const [flowFromX, flowFromY] = pol(windDir, R - 8)
  const [flowToX, flowToY] = pol(windDir, 10)
  const flowPath = `M ${flowFromX},${flowFromY} L ${flowToX},${flowToY}`
  const showFlow = windSpeed > 0.5
  const FLOW_DUR = 2.6
  const flowDelays = [0, -FLOW_DUR / 3, -(2 * FLOW_DUR) / 3]

  // crosswind component arrow — perpendicular to the runway, on the centreline
  const driftBrg = xw >= 0 ? runwayHdg - 90 : runwayHdg + 90
  const xwMag = Math.abs(xw)
  const xwLen = Math.min(74, xwMag * 4.5)
  const [xwAx, xwAy] = pol(approachBrg, RWY_LEN / 2 - 62)
  const xwEnd = pol(driftBrg, xwLen)
  const [xwEx, xwEy] = [xwAx + (xwEnd[0] - C), xwAy + (xwEnd[1] - C)]

  // aircraft silhouette at the start (approach threshold) of the selected runway
  const [planeX, planeY] = pol(recip, RWY_LEN / 2 - 6)

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width="100%"
      role="img"
      aria-label={`Runway ${desThis}, heading ${pad3(runwayHdg)}. Wind from ${pad3(windDir)} at ${Math.round(
        windSpeed,
      )} knots.`}
      style={{ display: 'block', maxWidth: 460, margin: '0 auto' }}
    >
      {/* compass face */}
      <circle cx={C} cy={C} r={R} fill={theme.palette.action.hover} stroke={faint} />
      <circle cx={C} cy={C} r={R - 34} fill="none" stroke={faint} strokeDasharray="2 4" />
      <line x1={C - R} y1={C} x2={C + R} y2={C} stroke={faint} strokeDasharray="4 5" />
      <line x1={C} y1={C - R} x2={C} y2={C + R} stroke={faint} strokeDasharray="4 5" />
      {ticks}
      {degLabels}
      {cardinals.map(([ch, brg]) => {
        const [x, y] = pol(brg, R - 42)
        return (
          <text
            key={ch}
            x={x}
            y={y}
            fontSize={13}
            fontWeight={700}
            fill={cardinal}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {ch}
          </text>
        )
      })}

      {/* runway */}
      <g transform={`rotate(${runwayHdg} ${C} ${C})`}>
        <rect x={C - RWY_W / 2} y={C - RWY_LEN / 2} width={RWY_W} height={RWY_LEN} rx={3} fill={rwyGrey} />
        <rect
          x={C - RWY_W / 2}
          y={landPrimary ? C : C - RWY_LEN / 2}
          width={RWY_W}
          height={RWY_LEN / 2}
          fill={CW_COLORS.intoWind}
          opacity={0.9}
        />
        {[-1, 1].map((s) =>
          [0, 1, 2, 3].map((i) => (
            <rect
              key={`${s}-${i}`}
              x={C - RWY_W / 2 + 3 + i * 6}
              y={s < 0 ? C - RWY_LEN / 2 + 4 : C + RWY_LEN / 2 - 16}
              width={3}
              height={12}
              fill="#fff"
            />
          )),
        )}
      </g>

      {/* runway numbers painted at each end */}
      {(
        [
          [desThis, recip],
          [desRecip, runwayHdg],
        ] as [string, number][]
      ).map(([des, brg]) => {
        const [x, y] = pol(brg, RWY_LEN / 2 - 30)
        // Painted so the pilot landing on it reads it upright: the top of
        // the digits points up the runway, away from the threshold.
        return (
          <text
            key={des}
            transform={`translate(${x} ${y}) rotate(${(brg + 180) % 360})`}
            fontSize={15}
            fontWeight={800}
            fill="#fff"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ paintOrder: 'stroke', stroke: '#2b353d', strokeWidth: 1.25 }}
          >
            {des}
          </text>
        )
      })}

      {/* aircraft on the runway, nose toward the selected heading */}
      <g transform={`translate(${planeX} ${planeY}) rotate(${runwayHdg}) scale(1.5)`}>
        <path d={PLANE_PATH} fill={CW_COLORS.aircraft} stroke="#fff" strokeWidth={0.7} />
      </g>

      {/* crosswind component — static shaft + animated double-chevrons */}
      {xwMag > 0.4 && (
        <line
          x1={xwAx}
          y1={xwAy}
          x2={xwEx}
          y2={xwEy}
          stroke={CW_COLORS.crosswind}
          strokeWidth={2.4}
          strokeLinecap="round"
        />
      )}
      {xwMag > 0.4 &&
        [0, -0.7].map((delay, i) => (
          <g key={i}>
            <animateMotion
              dur="1.4s"
              repeatCount="indefinite"
              rotate="auto"
              begin={`${delay}s`}
              path={`M ${xwAx},${xwAy} L ${xwEx},${xwEy}`}
            />
            <g>
              <animateTransform
                attributeName="transform"
                type="scale"
                values="0.7;1.25"
                dur="1.4s"
                repeatCount="indefinite"
                begin={`${delay}s`}
              />
              <polyline
                points="-7,-6 0,0 -7,6"
                fill="none"
                stroke={CW_COLORS.crosswind}
                strokeWidth={2.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.18;0.72;1"
                  dur="1.4s"
                  repeatCount="indefinite"
                  begin={`${delay}s`}
                />
              </polyline>
              <polyline
                points="-16,-6 -9,0 -16,6"
                fill="none"
                stroke={CW_COLORS.crosswind}
                strokeWidth={2.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.18;0.72;1"
                  dur="1.4s"
                  repeatCount="indefinite"
                  begin={`${delay}s`}
                />
              </polyline>
            </g>
          </g>
        ))}

      {/* wind — static shaft from the source rim, animated double-chevrons streaming downwind */}
      <line
        x1={wShaftX1}
        y1={wShaftY1}
        x2={wShaftX2}
        y2={wShaftY2}
        stroke={CW_COLORS.wind}
        strokeWidth={2.4}
        strokeLinecap="round"
      />
      {showFlow &&
        flowDelays.map((delay, i) => (
          <g key={i}>
            <animateMotion
              dur={`${FLOW_DUR}s`}
              repeatCount="indefinite"
              rotate="auto"
              begin={`${delay}s`}
              path={flowPath}
            />
            <g>
              <animateTransform
                attributeName="transform"
                type="scale"
                values="1.2;0.4"
                dur={`${FLOW_DUR}s`}
                repeatCount="indefinite"
                begin={`${delay}s`}
              />
              <polyline
                points="-8,-7 0,0 -8,7"
                fill="none"
                stroke={CW_COLORS.wind}
                strokeWidth={2.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.12;0.72;1"
                  dur={`${FLOW_DUR}s`}
                  repeatCount="indefinite"
                  begin={`${delay}s`}
                />
              </polyline>
              <polyline
                points="-18,-7 -10,0 -18,7"
                fill="none"
                stroke={CW_COLORS.wind}
                strokeWidth={2.6}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;1;0"
                  keyTimes="0;0.12;0.72;1"
                  dur={`${FLOW_DUR}s`}
                  repeatCount="indefinite"
                  begin={`${delay}s`}
                />
              </polyline>
            </g>
          </g>
        ))}
    </svg>
  )
}
